import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CreateNewTopupPage from "../../../entities/SuperAdminPage/CreateNewTopup";
import { planService, topupService } from "../../../api";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";
import {
  buildTopupPayload,
  validateTopupPack,
} from "../../../constants/planConstants";

const EMPTY_FORM = {
  name: "",
  allowances: {},
  packType: "fixed",
  minQuantity: "",
  maxQuantity: "",
  availableToPlans: [],
};

const CreateNewTopup = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [pricingList, setPricingList] = useState([]);
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const selectedCountry = String(location?.state?.country || "").trim();

  const normalizeListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result)) return response.result;
    return [];
  };

  useEffect(() => {
    fetchPlans();
    if (isEditMode) {
      fetchTopup();
    }
  }, [id]);

  const fetchPlans = async () => {
    try {
      const result = await planService.list();
      setPlans(normalizeListResponse(result));
    } catch (error) {
      showAlert(error?.message || "Could not load plans", "error");
    }
  };

  const fetchTopup = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await topupService.getById(id);
      if (result?.success === false) {
        showAlert(result?.message || "Topup pack not found", "error");
        LoaderHelper.loaderStatus(false);
        navigate("/dashboard/topupManagement", {
          state: selectedCountry ? { country: selectedCountry } : undefined,
        });
        return;
      }

      const pack = result?.data || result || {};
      setForm({
        name: pack?.name ?? "",
        allowances:
          pack?.allowances && typeof pack.allowances === "object" ? pack.allowances : {},
        packType: pack?.packType ?? "fixed",
        minQuantity: pack?.minQuantity ?? "",
        maxQuantity: pack?.maxQuantity ?? "",
        availableToPlans: Array.isArray(pack?.availableToPlans)
          ? pack.availableToPlans
          : [],
      });
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error loading topup pack", "error");
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Checking a module's box adds its key to the allowances map (starting blank);
  // unchecking removes the key entirely — an absent key means "this pack doesn't
  // grant that module at all," which is different from a present key with a
  // blank/null value ("grants it, unlimited").
  const handleToggleAllowance = (key, checked) => {
    setForm((prev) => {
      const nextAllowances = { ...prev.allowances };
      if (checked) {
        nextAllowances[key] = "";
      } else {
        delete nextAllowances[key];
      }
      return { ...prev, allowances: nextAllowances };
    });
    setErrors((prev) => ({ ...prev, allowances: undefined, [`allowance_${key}`]: undefined }));
  };

  const handleAllowanceValueChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      allowances: { ...prev.allowances, [key]: value },
    }));
    setErrors((prev) => ({ ...prev, [`allowance_${key}`]: undefined }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateTopupPack(form);

    // Pricing is only part of creation; in edit mode it is managed through the
    // dedicated pricing endpoints instead.
    if (!isEditMode && pricingList.length === 0) {
      // PlanPricingEditor holds the in-progress row in its own state until
      // "Add country" is pressed, so filled-but-uncommitted inputs still leave
      // pricingList empty. Say what to actually do rather than just "add one".
      validationErrors.pricing =
        "Enter a country, currency and price, then press \"Add country\" to add it to the list.";
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      showAlert("Please correct the highlighted fields.", "error");
      return;
    }

    const payload = buildTopupPayload(form);
    setSubmitting(true);
    LoaderHelper.loaderStatus(true);

    try {
      let result;
      if (isEditMode) {
        // Entitlement fields only — pricing never goes through PUT.
        result = await topupService.update(id, payload);
      } else {
        result = await topupService.create({ ...payload, pricing: pricingList });
      }

      if (result?.success === true) {
        showAlert(
          result?.message ||
            `Topup pack ${isEditMode ? "updated" : "created"} successfully`,
          "success"
        );
        navigate("/dashboard/topupManagement", {
          state: selectedCountry ? { country: selectedCountry } : undefined,
        });
      } else {
        showAlert(
          result?.message ||
            `Failed to ${isEditMode ? "update" : "create"} topup pack`,
          "error"
        );
      }
      LoaderHelper.loaderStatus(false);
      setSubmitting(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      setSubmitting(false);
      showAlert(
        error?.message ||
          `Error ${isEditMode ? "updating" : "creating"} topup pack`,
        "error"
      );
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/topupManagement", {
      state: selectedCountry ? { country: selectedCountry } : undefined,
    });
  };

  return (
    <CreateNewTopupPage
      isEditMode={isEditMode}
      topupId={id}
      form={form}
      errors={errors}
      plans={plans}
      pricingList={pricingList}
      submitting={submitting}
      onChange={handleChange}
      onToggleAllowance={handleToggleAllowance}
      onAllowanceValueChange={handleAllowanceValueChange}
      onPricingChange={setPricingList}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

export default CreateNewTopup;
